import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";

/**
 * Katalog scénářů — `/admin/api/v1/testing/scenarios/**`.
 *
 * Scénář je deklarativní YAML dokument, který lze buď spustit živě v ETTS (integrační
 * test s průběhem přes SSE), nebo vyexportovat pro externí nástroj (JMeter, SoapUI,
 * Postman, k6). Obě cesty čtou tentýž plán, takže dělají prokazatelně totéž.
 */

export type ScenarioStatus = "AVAILABLE" | "UNAVAILABLE" | "INVALID";

export interface ScenarioSummary {
  id: string;
  title: string;
  description: string | null;
  tags: string[];
  status: ScenarioStatus;
  /** Proč scénář v tomto buildu nejde použít. Prázdné u AVAILABLE. */
  unavailableReasons: string[];
  /** false = některý krok míří mimo ETTS → nesmí se exportovat jako zátěžový test. */
  loadSafe: boolean;
  stepCount: number;
  origin: string;
}

export interface PlanDiagnostic {
  severity: "ERROR" | "WARNING";
  code: string;
  scenarioId: string | null;
  stepId: string | null;
  message: string;
  detail: string | null;
  origin: string | null;
}

export interface ScenarioDetail {
  summary: ScenarioSummary;
  scenario: {
    id: string;
    inputs?: {
      name: string;
      kind: string;
      source: string;
      required: boolean;
      secret: boolean;
      defaultValue: string | null;
      options: string[];
    }[];
  };
  diagnostics: PlanDiagnostic[];
}

export interface ExportSystem {
  id: string;
  displayName: string;
  /** Režimy, které nástroj umí vyjádřit — SoapUI např. jen FUNCTIONAL. */
  modes: string[];
}

export function useScenarios() {
  return useQuery({
    queryKey: ["scenarios", "list"],
    queryFn: () => apiFetch<ScenarioSummary[]>("/admin/api/v1/testing/scenarios"),
  });
}

export function useScenarioDetail(id: string | null) {
  return useQuery({
    queryKey: ["scenarios", "detail", id],
    queryFn: () => apiFetch<ScenarioDetail>(`/admin/api/v1/testing/scenarios/${id}`),
    enabled: !!id,
  });
}

export function useExportSystems() {
  return useQuery({
    queryKey: ["scenarios", "systems"],
    queryFn: () => apiFetch<ExportSystem[]>("/admin/api/v1/testing/scenarios/systems"),
  });
}

/** Dokumenty, které se vůbec nepodařilo přečíst — nepatří k žádnému scénáři. */
export function useCatalogProblems() {
  return useQuery({
    queryKey: ["scenarios", "problems"],
    queryFn: () => apiFetch<PlanDiagnostic[]>("/admin/api/v1/testing/scenarios/problems"),
  });
}

export interface TtlOption {
  id: string;
  label: string;
}

export interface IssuedKey {
  keyId: string;
  /** Zobrazí se jednou a nikde se neukládá v čitelné podobě. */
  value: string;
  expiresAt: string;
  /** Odvozeno z endpointů, které scénář volá — ne hádané. */
  scopes: string[];
  /** Kroky, jejichž oprávnění se nepodařilo určit; klíč na ně nemusí stačit. */
  unresolved: string[];
}

export function useApiKeyTtls() {
  return useQuery({
    queryKey: ["scenarios", "api-key-ttls"],
    queryFn: () => apiFetch<TtlOption[]>("/admin/api/v1/testing/scenarios/api-key/ttls"),
  });
}

/** Vystaví krátkodobý API klíč s oprávněními právě pro tento scénář. */
export async function issueScenarioApiKey(
  id: string,
  ttl: string,
): Promise<{ ok: true; key: IssuedKey } | { ok: false; error: string }> {
  try {
    const key = await apiFetch<IssuedKey>(`/admin/api/v1/testing/scenarios/${id}/api-key`, {
      method: "POST",
      json: { ttl },
    });
    return { ok: true, key };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

export interface ScenarioEvent {
  kind: "STARTED" | "STEP_STARTED" | "STEP_FINISHED" | "COMPLETED" | "FAILED";
  scenarioId: string;
  stepId: string | null;
  stepTitle: string | null;
  stepNumber: number;
  totalSteps: number;
  outcome: {
    result: "OK" | "FAILED" | "SKIPPED";
    httpStatus: number;
    durationMs: number;
    assertions: { description: string; passed: boolean; detail: string }[];
    error: string | null;
  } | null;
  variables: Record<string, string>;
  error: string | null;
  timestamp: string;
}

/**
 * Spustí scénář a streamuje průběh.
 *
 * Čte přes `fetch` + `ReadableStream`, ne přes `EventSource` — ten neumí POST ani poslat
 * tělo, a hlavně by nedokázal předat vstupy scénáře.
 */
export async function runScenario(
  id: string,
  inputs: Record<string, string>,
  onEvent: (event: ScenarioEvent) => void,
): Promise<{ ok: boolean; error?: string }> {
  const res = await fetch(`/admin/api/v1/testing/scenarios/${id}/run`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify({ inputs }),
  });

  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => null);
    return { ok: false, error: text || `HTTP ${res.status}` };
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE rámce jsou oddělené prázdným řádkem; poslední, neúplný zůstává v bufferu.
    const frames = buffer.split("\n\n");
    buffer = frames.pop() ?? "";
    for (const frame of frames) {
      const data = frame
        .split("\n")
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).trim())
        .join("");
      if (!data) continue;
      try {
        onEvent(JSON.parse(data) as ScenarioEvent);
      } catch {
        // Nekompletní rámec — přeskočíme, další čtení ho doplní.
      }
    }
  }
  return { ok: true };
}

/** Stáhne artefakty scénáře pro zvolený nástroj jako ZIP. */
export async function downloadScenarioExport(
  id: string,
  targetSystem: string,
  mode: string,
): Promise<{ ok: boolean; error?: string }> {
  const res = await fetch(`/admin/api/v1/testing/scenarios/${id}/export`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ targetSystem, mode }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => null);
    let message = text || `HTTP ${res.status}`;
    try {
      const parsed = JSON.parse(message) as { message?: string; error?: string };
      message = parsed.message || parsed.error || message;
    } catch {
      // Odpověď nebyla JSON — necháme text tak, jak přišel.
    }
    return { ok: false, error: message };
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `etts-${id}-${targetSystem}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { ok: true };
}
